import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import BaseModal from '../common/BaseModal';
import { accountTypes } from '../../constants/accountTypes';

const EditAccountModal = ({ isOpen, onClose, account, onAccountUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    account_type: 'brokerage',
    institution: '',
    account_number: '',
    balance: '',
    starting_balance: '',
    currency: 'USD',
    sync_enabled: false,
    is_active: true,
    payment_due_day: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update form data when account changes
  useEffect(() => {
    if (account && isOpen) {
      setFormData({
        name: account.name || '',
        account_type: account.account_type || 'brokerage',
        institution: account.institution || '',
        account_number: account.account_number || '',
        balance: account.balance ? account.balance.toString() : '',
        starting_balance: account.starting_balance ? account.starting_balance.toString() : '',
        currency: account.currency || 'USD',
        sync_enabled: account.sync_enabled || false,
        is_active: account.is_active !== undefined ? account.is_active : true,
        payment_due_day: account.payment_due_day ? account.payment_due_day.toString() : ''
      });
      setError(null);
    }
  }, [account, isOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name) {
        throw new Error('Account name is required');
      }

      // Institution is required for all account types except cash
      if (formData.account_type !== 'cash' && !formData.institution) {
        throw new Error('Institution is required for this account type');
      }

      // Format starting balance as number (balance calculated dynamically, not stored)
      const accountData = {
        ...formData,
        institution: formData.institution || 'Cash',
        starting_balance: formData.starting_balance ? parseFloat(formData.starting_balance) : 0.0,
        // Credit card specific - only include if it's a credit card
        payment_due_day: formData.account_type === 'credit_card' && formData.payment_due_day
          ? parseInt(formData.payment_due_day)
          : null
        // Don't send balance - it's calculated from starting_balance + transactions
      };

      await onAccountUpdated(account.id, accountData);
      onClose();
    } catch (err) {
      console.error('Error updating account:', err);
      setError(err.message || 'Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  if (!account) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={loading ? () => {} : handleClose}
      size="md"
      showCloseButton={false}
      closeOnBackdrop={!loading}
    >
      <div className="flex items-center justify-between p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Edit Account</h2>
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-700 text-sm">{error}</div>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Account Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="e.g., Main Checking, Fidelity Brokerage"
            />
          </div>

          <div>
            <label htmlFor="account_type" className="block text-sm font-medium text-gray-700 mb-2">
              Account Type *
            </label>
            <select
              id="account_type"
              name="account_type"
              required
              value={formData.account_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {accountTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {formData.account_type !== 'cash' && (
            <>
              <div>
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-2">
                  Institution *
                </label>
                <input
                  type="text"
                  id="institution"
                  name="institution"
                  required
                  value={formData.institution}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Chase, Fidelity, Bank of America"
                />
              </div>

              <div>
                <label htmlFor="account_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number (optional)
                </label>
                <input
                  type="text"
                  id="account_number"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., ****1234"
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="balance" className="block text-sm font-medium text-gray-700 mb-2">
              Current Balance
            </label>
            <input
              type="number"
              id="balance"
              name="balance"
              step="0.01"
              value={formData.balance}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="0.00"
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">Calculated from starting balance + transactions</p>
          </div>

          <div>
            <label htmlFor="starting_balance" className="block text-sm font-medium text-gray-700 mb-2">
              Starting Balance
            </label>
            <input
              type="number"
              id="starting_balance"
              name="starting_balance"
              step="0.01"
              value={formData.starting_balance}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Balance before your first imported transaction</p>
          </div>

          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="JPY">JPY - Japanese Yen</option>
            </select>
          </div>

          {/* Credit Card Specific Fields */}
          {formData.account_type === 'credit_card' && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">Credit Card Settings</h4>
              <div>
                <label htmlFor="payment_due_day" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Due Day of Month
                </label>
                <select
                  id="payment_due_day"
                  name="payment_due_day"
                  value={formData.payment_due_day}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Not set</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  The day of each month when your payment is due. If this falls on a weekend, the due date will be the next business day.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                id="sync_enabled"
                name="sync_enabled"
                type="checkbox"
                checked={formData.sync_enabled}
                onChange={handleInputChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="sync_enabled" className="ml-2 block text-sm text-gray-900">
                Enable automatic sync
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Account is active
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Account'}
            </button>
          </div>
        </form>
    </BaseModal>
  );
};

export default EditAccountModal;